import {
  type DateValue,
  parseAbsolute,
  toCalendarDateTime,
  today,
  toZoned,
} from "@internationalized/date";
import type { Preset } from "@ui/DateRangePicker/PresetSelector";
import t from "../../../../i18n";

export const DATE_RANGE_NAVIGATION_PRESETS: { label: string; value: string }[] = [
  {
    label: t("period.selector.last_group"),
    value: "CYCLE",
  },
  {
    label: t("period.selector.3w.long"),
    value: "P3W",
  },
  {
    label: t("period.selector.3m.long"),
    value: "P3M",
  },
  {
    label: t("period.selector.1y.long"),
    value: "P1Y",
  },
  {
    label: t("period.selector.all"),
    value: "ALL",
  },
];

export function getDateRangeNavigationPresets({
  from,
  to,
  cycle,
  timezone,
}: {
  from?: number;
  to?: number;
  cycle?: { from?: number; to?: number };
  timezone: string;
}): Preset<DateValue>[] {
  const start = from ? parseAbsolute(new Date(from).toISOString(), timezone) : today(timezone);
  const end = to ? parseAbsolute(new Date(to).toISOString(), timezone) : today(timezone);

  const cycleStart = cycle?.from
    ? parseAbsolute(new Date(cycle.from).toISOString(), timezone)
    : null;
  const cycleEnd = cycle?.to ? parseAbsolute(new Date(cycle.to).toISOString(), timezone) : null;

  const presets = [
    ...(cycleStart && cycleEnd
      ? [
          {
            metadata: { id: "CYCLE" },
            label: t("period.selector.last_group"),
            startDate: () => cycleStart,
            endDate: () => cycleEnd,
          },
        ]
      : []),
    {
      metadata: { id: "P3W" },
      label: t("period.selector.3w.long"),
      startDate: () => toZoned(toCalendarDateTime(end).add({ weeks: -3 }), timezone),
      endDate: () => toZoned(toCalendarDateTime(end), timezone),
    },
    {
      metadata: { id: "P3M" },
      label: t("period.selector.3m.long"),
      startDate: () => toZoned(toCalendarDateTime(end).add({ months: -3 }), timezone),
      endDate: () => toZoned(toCalendarDateTime(end), timezone),
    },
    {
      metadata: { id: "P1Y" },
      label: t("period.selector.1y.long"),
      startDate: () => toZoned(toCalendarDateTime(end).add({ years: -1 }), timezone),
      endDate: () => toZoned(toCalendarDateTime(end), timezone),
    },
    ...(from && to
      ? [
          {
            metadata: { id: "ALL" },
            label: t("period.selector.all"),
            startDate: () => start,
            endDate: () => end,
          },
        ]
      : []),
  ];

  return presets;
}

export function convertPresetToPeriod(
  preset: Preset<DateValue>,
  timezone: string,
): { from: number; to: number } {
  return {
    from: (typeof preset.startDate === "function" ? preset.startDate() : preset.startDate)
      .toDate(timezone)
      .getTime(),
    to: (typeof preset.endDate === "function" ? preset.endDate() : preset.endDate)
      .toDate(timezone)
      .getTime(),
  };
}
